import { CONTACT_STATES } from '../../utils/constants';
import { createEmptyContactDataPoint, } from './types';
const EMAIL_TYPE_TO_LABEL = {
    work: 'Work',
    direct: 'Direct',
    email: 'Email',
};
const setEmailType = (item) => {
    const type = item.type;
    const label = item.label;
    const isRevealed = item.is_revealed;
    const isWorkEmail = item.is_work_email;
    if (type && EMAIL_TYPE_TO_LABEL[type]) {
        return EMAIL_TYPE_TO_LABEL[type];
    }
    if (label && EMAIL_TYPE_TO_LABEL[label]) {
        return EMAIL_TYPE_TO_LABEL[label];
    }
    if (isRevealed && isWorkEmail === null) {
        return EMAIL_TYPE_TO_LABEL.email;
    }
    return isWorkEmail ? EMAIL_TYPE_TO_LABEL.work : EMAIL_TYPE_TO_LABEL.direct;
};
const getEmailConfidence = (qualityScore) => {
    if (!qualityScore) {
        return null;
    }
    if (qualityScore > 75) {
        return 'A+';
    }
    return 'A';
};
const parseEmailData = (email, original) => {
    var _a, _b, _c, _d, _e;
    if (!email) {
        return createEmptyContactDataPoint();
    }
    const e = email;
    return Object.assign(Object.assign({}, createEmptyContactDataPoint()), { value: e.address || e.email || '', type: setEmailType(e), isRevealed: Boolean(e.is_revealed || e.isShown || e.status === 'shown'), isEdited: Boolean(e.isEdited || e.is_edited), isManuallyAdded: Boolean(e.isManuallyAdded || e.is_manually_added), feedback: (_a = e.feedback) !== null && _a !== void 0 ? _a : null, emailConfidence: getEmailConfidence(e.quality_score), id: e.id || (original === null || original === void 0 ? void 0 : original.id) || null, shownDate: e.shown_date || (original === null || original === void 0 ? void 0 : original.shownDate) || null, status: e.status || (original === null || original === void 0 ? void 0 : original.status) || null, shown_by: e.shown_by || (original === null || original === void 0 ? void 0 : original.shown_by), vendor: (_c = (_b = e.vendor) !== null && _b !== void 0 ? _b : original === null || original === void 0 ? void 0 : original.vendor) !== null && _c !== void 0 ? _c : null, vendors: (_e = (_d = e.vendors) !== null && _d !== void 0 ? _d : original === null || original === void 0 ? void 0 : original.vendors) !== null && _e !== void 0 ? _e : [] });
};
const parsePhoneData = (phone, defaultLabel = 'Phone', original) => {
    var _a, _b, _c, _d, _e;
    if (!phone) {
        return createEmptyContactDataPoint();
    }
    const p = phone;
    const status = p.status || (original === null || original === void 0 ? void 0 : original.status) || null;
    return Object.assign(Object.assign({}, createEmptyContactDataPoint()), { 
        // a missed datapoint has no value to fall back to, so it stays empty instead of showing a dash
        value: p.text || p.phone || (p.number != null ? String(p.number) : '') || (status === CONTACT_STATES.MISS ? '' : '-'), type: p.type || defaultLabel, isEdited: Boolean(p.isEdited || p.is_edited), isManuallyAdded: Boolean(p.isManuallyAdded || p.is_manually_added), doNotCall: p.do_not_call || null, isDncPhone: p.is_do_not_call || false, feedback: (_a = p.feedback) !== null && _a !== void 0 ? _a : null, id: p.id || (original === null || original === void 0 ? void 0 : original.id) || null, status, shownDate: p.shown_date || (original === null || original === void 0 ? void 0 : original.shownDate) || null, isRevealed: Boolean(p.is_revealed || (original === null || original === void 0 ? void 0 : original.isRevealed) || p.isShown || p.status === 'shown'), shown_by: p.shown_by || (original === null || original === void 0 ? void 0 : original.shown_by), vendor: (_c = (_b = p.vendor) !== null && _b !== void 0 ? _b : original === null || original === void 0 ? void 0 : original.vendor) !== null && _c !== void 0 ? _c : null, vendors: (_e = (_d = p.vendors) !== null && _d !== void 0 ? _d : original === null || original === void 0 ? void 0 : original.vendors) !== null && _e !== void 0 ? _e : [] });
};
const getContactLocation = (location, contactLocation) => {
    if (location) {
        return location;
    }
    if (contactLocation) {
        return `${contactLocation.city ? `${contactLocation.city}, ` : ''}${contactLocation.country || ''}`;
    }
    return '';
};
export const mapIntegrationsData = (integrations) => {
    if (!integrations) {
        return {};
    }
    const result = {};
    Object.entries(integrations).forEach(([key, value]) => {
        var _a, _b;
        const integration = value;
        if (!((_a = integration === null || integration === void 0 ? void 0 : integration.errors) === null || _a === void 0 ? void 0 : _a.length) && (integration === null || integration === void 0 ? void 0 : integration.link)) {
            result[key] = Object.assign(Object.assign({ link: integration.link, type: (_b = integration.type) !== null && _b !== void 0 ? _b : null }, (integration.outdatedSince && { outdatedSince: integration.outdatedSince })), (integration.lastUpdated && { lastUpdated: integration.lastUpdated }));
        }
    });
    return result;
};
const toIdString = (id) => {
    if (typeof id === 'string' || typeof id === 'number') {
        return String(id);
    }
    return '';
};
const toStringArray = (value) => {
    if (Array.isArray(value)) {
        return value.map(toIdString).filter(Boolean);
    }
    if (value && typeof value === 'object' && typeof value.slice === 'function') {
        const sliced = value.slice();
        if (Array.isArray(sliced)) {
            return sliced.map(toIdString).filter(Boolean);
        }
    }
    return [];
};
/** Collect unique datapoint ids from integration payloads. Mirrors MST Contact.setSavedDatapoints. */
export const collectSavedDatapointIds = (integrations, existing = []) => {
    if (!integrations) {
        return [...existing];
    }
    let values = [];
    if (Array.isArray(integrations)) {
        values = integrations;
    }
    else if (typeof integrations === 'object') {
        values = Object.values(integrations);
    }
    const result = [...existing];
    values.forEach(item => {
        if (!item || typeof item !== 'object') {
            return;
        }
        const dataPoints = toStringArray(item.dataPoints);
        dataPoints.forEach(datapointId => {
            if (!result.includes(datapointId)) {
                result.push(datapointId);
            }
        });
    });
    return result;
};
const mapPreviousJob = (previousJob) => {
    var _a, _b, _c;
    if (!previousJob) {
        return { changedJob: false, details: null };
    }
    const details = previousJob.details;
    return {
        changedJob: Boolean(previousJob.changedJob),
        details: details
            ? {
                companyName: (_a = details.companyName) !== null && _a !== void 0 ? _a : null,
                timeSinceJobChange: (_b = details.timeSinceJobChange) !== null && _b !== void 0 ? _b : 0,
                timePeriod: (_c = details.timePeriod) !== null && _c !== void 0 ? _c : '',
            }
            : null,
    };
};
const mapTags = (tags) => {
    if (!(tags === null || tags === void 0 ? void 0 : tags.length)) {
        return [];
    }
    return tags.map(tag => {
        var _a;
        return ({
            id: tag.id,
            name: tag.name,
            color: (_a = tag.color) !== null && _a !== void 0 ? _a : '',
        });
    });
};
const lastListId = (listId) => {
    if (!(listId === null || listId === void 0 ? void 0 : listId.length)) {
        return '';
    }
    return listId.split(',').pop() || '';
};
const toPlainObject = (value) => {
    if (!value) {
        return null;
    }
    if (typeof value.toJSON === 'function') {
        return value.toJSON();
    }
    return value;
};
const normalizeWishlistEmail = (email) => {
    if (!email) {
        return null;
    }
    return {
        value: String(email.value || email.address || ''),
        type: String(email.type || 'work'),
    };
};
const normalizeWishlistPhone = (phone) => {
    if (!phone) {
        return null;
    }
    return {
        value: String(phone.value || phone.text || phone.number || ''),
        type: String(phone.type || 'work'),
    };
};
const toObjectArray = (value) => {
    let items = [];
    if (Array.isArray(value)) {
        items = value;
    }
    else if (value && typeof value === 'object' && typeof value.slice === 'function') {
        const sliced = value.slice();
        if (Array.isArray(sliced)) {
            items = sliced;
        }
    }
    return items.filter((item) => Boolean(item) && typeof item === 'object');
};
/** Mirrors MST Contact.changes normalization. */
export const mapContactChanges = (changes) => {
    if (!Array.isArray(changes) || !changes.length) {
        return [];
    }
    return changes.map((change) => ({
        fieldKey: String((change === null || change === void 0 ? void 0 : change.fieldKey) || ''),
        source: String((change === null || change === void 0 ? void 0 : change.source) || ''),
        status: String((change === null || change === void 0 ? void 0 : change.status) || ''),
        oldValue: (change === null || change === void 0 ? void 0 : change.oldValue) == null ? null : String(change.oldValue),
        newValue: (change === null || change === void 0 ? void 0 : change.newValue) == null ? null : String(change.newValue),
    }));
};
/** Mirrors MST Contact.setWishlistAddedData email/phone normalization. */
export const mapWishlistAddedDataPoints = (data) => {
    const plain = toPlainObject(data) || data || {};
    const emails = toObjectArray(plain.emails)
        .map(normalizeWishlistEmail)
        .filter((item) => Boolean(item));
    const phones = toObjectArray(plain.phones)
        .map(normalizeWishlistPhone)
        .filter((item) => Boolean(item));
    return { emails, phones };
};
const applyDatapointFeedback = (dataPoint, feedback) => {
    var _a;
    if (!feedback || typeof feedback !== 'object') {
        return dataPoint;
    }
    const feedbackMap = feedback;
    return Object.assign(Object.assign({}, dataPoint), { feedback: (_a = feedbackMap[dataPoint.value]) !== null && _a !== void 0 ? _a : null });
};
/** Mirrors MST Contact.setContactEditableData email/phone/title/feedback updates. */
export const mapContactEditableData = (existing, data) => {
    var _a;
    const plain = toPlainObject(data) || data || {};
    const emails = toObjectArray(plain.emails);
    const phones = toObjectArray(plain.phones);
    let email1 = parseEmailData(emails[0], existing.email1);
    let email2 = parseEmailData(emails[1], existing.email2);
    let phone1 = parsePhoneData(phones[0], 'Phone', existing.phone1);
    let phone2 = parsePhoneData(phones[1], 'Phone', existing.phone2);
    if (plain.feedback) {
        email1 = applyDatapointFeedback(email1, plain.feedback);
        email2 = applyDatapointFeedback(email2, plain.feedback);
        phone1 = applyDatapointFeedback(phone1, plain.feedback);
        phone2 = applyDatapointFeedback(phone2, plain.feedback);
    }
    const changes = { email1, email2, phone1, phone2 };
    const jobTitle = plain.jobTitle;
    if (jobTitle && typeof jobTitle === 'object') {
        const titleData = jobTitle;
        changes.title = (_a = titleData.title) !== null && _a !== void 0 ? _a : '';
        changes.editedFields = Array.isArray(titleData.editedFields) ? titleData.editedFields.map(item => String(item)) : [];
    }
    return changes;
};
const mapWishlistCompany = (company) => {
    var _a, _b, _c, _d, _e, _f;
    if (!company) {
        return null;
    }
    const plain = toPlainObject(company) || company;
    const headquarter = (plain.headquarter || plain.location || {});
    const companySize = (plain.company_size || { min: plain.min, max: plain.max });
    const domains = (plain.domains || {});
    const social = (plain.social || {});
    const industry = (plain.industry || {});
    const revenueRange = (plain.revenue_range || plain.revenueRange);
    return {
        id: String(plain.id || 'wishlist'),
        name: String(plain.name || ''),
        hasCompany: Boolean((_a = plain.hasCompany) !== null && _a !== void 0 ? _a : true),
        city: String(headquarter.city || plain.city || ''),
        region: String(headquarter.region || plain.region || ''),
        country: String(headquarter.country || plain.country || ''),
        min: (companySize === null || companySize === void 0 ? void 0 : companySize.min) || Number(plain.min) || 0,
        max: (companySize === null || companySize === void 0 ? void 0 : companySize.max) || Number(plain.max) || 0,
        about: String(plain.about || plain.description || ''),
        logoUrl: String(plain.logoUrl || plain.logo_url || ''),
        domain: String(plain.domain || domains.homepage || 'Company Website'),
        homePageUrl: String(plain.homePageUrl || plain.homepage_url || ''),
        founded: plain.founded != null ? String(plain.founded) : '',
        specialities: (plain.specialities || plain.industry_tags || []),
        mainIndustry: String(plain.mainIndustry || ((_b = industry === null || industry === void 0 ? void 0 : industry.primary_industry) === null || _b === void 0 ? void 0 : _b.key) || ''),
        facebook: String(plain.facebook || social.facebook || ''),
        twitter: String(plain.twitter || social.twitter || ''),
        LinkedIn: String(plain.LinkedIn || social.LinkedIn || social.linkedin || ''),
        companyResultString: String(plain.companyResultString || JSON.stringify(plain)),
        revenueRange: revenueRange && typeof revenueRange === 'object' ? Object.assign({}, revenueRange) : {},
        companyLid: ((_d = (_c = plain.companyLid) !== null && _c !== void 0 ? _c : plain.company_lid) !== null && _d !== void 0 ? _d : null),
        lushaCompanyId: ((_f = (_e = plain.lushaCompanyId) !== null && _e !== void 0 ? _e : plain.lusha_company_id) !== null && _f !== void 0 ? _f : null),
        isBlockedForShow: Boolean(plain.isBlockedForShow),
        isSocial: Boolean(plain.isSocial || plain.social),
    };
};
const mapWishlistIntegrations = (integrations) => {
    if (!integrations || Array.isArray(integrations)) {
        return {};
    }
    const plain = toPlainObject(integrations) || {};
    return Object.entries(plain).reduce((acc, [key, value]) => {
        var _a, _b, _c;
        if (!value) {
            return acc;
        }
        acc[key] = {
            link: (_a = value.link) !== null && _a !== void 0 ? _a : null,
            name: (_b = value.name) !== null && _b !== void 0 ? _b : null,
            type: (_c = value.type) !== null && _c !== void 0 ? _c : null,
        };
        return acc;
    }, {});
};
/**
 * Maps API / MST wishlist contact payload to Redux WishlistData.
 * Mirrors MST Contact.setWishlistContact normalization.
 */
export const mapApiWishlistContactToWishlistData = (wishlistContact) => {
    var _a;
    if (!wishlistContact) {
        return null;
    }
    const plain = toPlainObject(wishlistContact) || wishlistContact;
    const defaultList = plain.listId ? [String(plain.listId)] : [];
    const rawEmails = plain.emails || [];
    const rawPhones = plain.phones || [];
    const normalizedEmails = rawEmails.map(normalizeWishlistEmail).filter(Boolean);
    const normalizedPhones = rawPhones.map(normalizeWishlistPhone).filter(Boolean);
    const jobTitle = plain.jobTitle;
    const resolvedJobTitle = typeof jobTitle === 'object' && jobTitle !== null ? ((_a = jobTitle.title) !== null && _a !== void 0 ? _a : '') : String(jobTitle || '');
    return {
        id: String(plain.id || ''),
        firstName: String(plain.firstName || ''),
        lastName: String(plain.lastName || ''),
        jobTitle: resolvedJobTitle,
        emails: normalizedEmails,
        phones: normalizedPhones,
        email1: normalizedEmails[0] || null,
        email2: normalizedEmails[1] || null,
        phone1: normalizedPhones[0] || null,
        phone2: normalizedPhones[1] || null,
        company: mapWishlistCompany(plain.company || null),
        integrations: mapWishlistIntegrations(plain.integrations),
        lists: (plain.contactLists || plain.lists || defaultList).map(String),
    };
};
export const mapSearchContactToContact = (contact, extras = {}) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { id, fullName, emails = [], phones = [], jobTitle, location, isBlockedForShow, isShown, tags, contactId, listId, socialLink, integrations, previousJob, canShowContact, maskedContactId, personId = null, isFreshData = false, freshDataSummary = null, uniqueSearchId, isAvailableForUnshownSave = false, firstName, companyLid, ordinalId = -1, wishlistContact, isPartialProfile = false, } = contact;
    const resolvedContactId = (isShown ? contactId || '' : contactId || id) || id;
    const resolvedListId = lastListId(listId);
    // Mirror MST setSearchV2Contact wishlist initialization
    let wishlistData = null;
    if (!isShown && wishlistContact) {
        wishlistData = mapApiWishlistContactToWishlistData(wishlistContact);
    }
    else if (!isShown && listId && listId.length > 0 && isPartialProfile) {
        wishlistData = mapApiWishlistContactToWishlistData(Object.assign(Object.assign({}, contact), { id: contactId || id, listId: resolvedListId, company: (_a = contact.company) !== null && _a !== void 0 ? _a : null }));
    }
    return {
        id,
        contactId: (wishlistData === null || wishlistData === void 0 ? void 0 : wishlistData.id) || resolvedContactId,
        maskedContactId: maskedContactId || '',
        maskedId: (_b = extras.maskedId) !== null && _b !== void 0 ? _b : null,
        personId: personId !== null && personId !== void 0 ? personId : null,
        uniqueSearchId: uniqueSearchId || '',
        requestId: extras.requestId || '',
        ordinalId,
        fullName: fullName !== null && fullName !== void 0 ? fullName : '',
        firstName: firstName !== null && firstName !== void 0 ? firstName : '',
        title: (_c = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.title) !== null && _c !== void 0 ? _c : '',
        department: ((_d = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.department) === null || _d === void 0 ? void 0 : _d[0]) || ((_e = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.departments) === null || _e === void 0 ? void 0 : _e[0]) || '',
        seniority: (_f = jobTitle === null || jobTitle === void 0 ? void 0 : jobTitle.seniority) !== null && _f !== void 0 ? _f : '',
        location: (location === null || location === void 0 ? void 0 : location.country) || '',
        socialLink: socialLink || '',
        email1: parseEmailData(emails[0]),
        email2: parseEmailData(emails[1]),
        phone1: parsePhoneData(phones[0]),
        phone2: parsePhoneData(phones[1]),
        isRevealed: Boolean(isShown),
        isPressShow: false,
        isOnboarding: Boolean(extras.isOnboarding),
        revealedBy: contact.revealedBy ||
            ((_g = contact.contact) === null || _g === void 0 ? void 0 : _g.revealedBy) ||
            null,
        selectedDataPointType: '',
        showCreditSplitPopup: false,
        creditSplitErrorType: '',
        canShowContact: canShowContact !== null && canShowContact !== void 0 ? canShowContact : null,
        isBlockedForShow: Boolean(isBlockedForShow),
        // Mirrors MST setSearchV2Contact: only a shown contact carries a show date.
        showDate: isShown ? contact.showDate || '' : '',
        contactList: isAvailableForUnshownSave || isShown ? resolvedListId : '',
        state: isShown ? CONTACT_STATES.SHOWN : CONTACT_STATES.NOT_SHOWN,
        tags: mapTags(tags),
        previousJob: mapPreviousJob(previousJob),
        isFreshData: Boolean(isFreshData),
        freshDataSummary: freshDataSummary !== null && freshDataSummary !== void 0 ? freshDataSummary : null,
        isAvailableForUnshownSave: Boolean(isAvailableForUnshownSave),
        integrationsData: mapIntegrationsData(integrations),
        companyLid: (_j = (_h = extras.companyLid) !== null && _h !== void 0 ? _h : companyLid) !== null && _j !== void 0 ? _j : null,
        companyName: extras.companyName || '',
        storeCompanyKey: (_k = extras.storeCompanyKey) !== null && _k !== void 0 ? _k : null,
        sequenceNum: 0,
        crmExistingIntegrations: [],
        savedDatapoints: isShown ? collectSavedDatapointIds(integrations) : [],
        wishlistData,
        isPartialProfile: Boolean(isPartialProfile),
        changes: mapContactChanges(contact.changes),
    };
};
export const mapEmployeeToContact = (employee, extras = {}) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { id, name, emails = [], phones = [], jobTitles = [], contact, ordinalId = -1, contactId: contactSearchId, maskedContactId, location, isBlockedForShow, contactSocialLinks, previousJob, isRevealed, canShowContact, maskId, personId, isFreshData = false, freshDataSummary = null, isShown, } = employee;
    const nested = contact || {};
    const nestedWithExtras = nested;
    const { showDate = '', lists = '', savedIntegrations = {}, tags, id: nestedContactId = '', unique_search_id: uniqueSearchId, location: contactLocation, revealedBy, } = nestedWithExtras;
    const [jobTitleSummary] = jobTitles;
    const resolvedContactId = nestedContactId || contactSearchId || id;
    const resolvedListId = lastListId(lists);
    const revealed = Boolean(showDate) || Boolean(isRevealed) || Boolean(isShown);
    const wishedContact = employee
        .wishedContact;
    const wishlistData = Array.isArray(wishedContact) && wishedContact.length > 0
        ? mapApiWishlistContactToWishlistData(wishedContact[0])
        : null;
    return {
        id,
        contactId: (wishlistData === null || wishlistData === void 0 ? void 0 : wishlistData.id) || resolvedContactId,
        maskedContactId: maskedContactId || '',
        maskedId: maskId || null,
        personId: personId !== null && personId !== void 0 ? personId : null,
        uniqueSearchId: uniqueSearchId || '',
        requestId: extras.requestId || '',
        ordinalId,
        fullName: name || '',
        firstName: ((_a = nested.name) === null || _a === void 0 ? void 0 : _a.first) || '',
        title: (jobTitleSummary === null || jobTitleSummary === void 0 ? void 0 : jobTitleSummary.title) || ((_b = nested.job_title) === null || _b === void 0 ? void 0 : _b.title) || '',
        department: ((_c = jobTitleSummary === null || jobTitleSummary === void 0 ? void 0 : jobTitleSummary.department) === null || _c === void 0 ? void 0 : _c[0]) || ((_e = (_d = nested.job_title) === null || _d === void 0 ? void 0 : _d.department) === null || _e === void 0 ? void 0 : _e[0]) || ((_g = (_f = nested.job_title) === null || _f === void 0 ? void 0 : _f.departments) === null || _g === void 0 ? void 0 : _g[0]) || '',
        seniority: (jobTitleSummary === null || jobTitleSummary === void 0 ? void 0 : jobTitleSummary.seniority) || ((_h = nested.job_title) === null || _h === void 0 ? void 0 : _h.seniority) || '',
        location: getContactLocation(location, contactLocation),
        socialLink: (contactSocialLinks === null || contactSocialLinks === void 0 ? void 0 : contactSocialLinks.linkedin) || ((_j = nested.social_links) === null || _j === void 0 ? void 0 : _j.linkedin) || '',
        email1: parseEmailData(emails[0] || ((_k = nested.emails) === null || _k === void 0 ? void 0 : _k[0])),
        email2: parseEmailData(emails[1] || ((_l = nested.emails) === null || _l === void 0 ? void 0 : _l[1])),
        phone1: parsePhoneData(phones[0] || ((_m = nested.phones) === null || _m === void 0 ? void 0 : _m[0])),
        phone2: parsePhoneData(phones[1] || ((_o = nested.phones) === null || _o === void 0 ? void 0 : _o[1])),
        isRevealed: revealed,
        isPressShow: false,
        isOnboarding: false,
        revealedBy: revealedBy || null,
        selectedDataPointType: '',
        showCreditSplitPopup: false,
        creditSplitErrorType: '',
        canShowContact: canShowContact !== null && canShowContact !== void 0 ? canShowContact : null,
        isBlockedForShow: Boolean(isBlockedForShow),
        showDate: showDate || '',
        contactList: resolvedListId,
        state: revealed ? CONTACT_STATES.SHOWN : CONTACT_STATES.NOT_SHOWN,
        tags: mapTags(tags),
        previousJob: mapPreviousJob(previousJob),
        isFreshData: Boolean(isFreshData),
        freshDataSummary: freshDataSummary !== null && freshDataSummary !== void 0 ? freshDataSummary : null,
        isAvailableForUnshownSave: false,
        integrationsData: mapIntegrationsData(savedIntegrations || nested.integrations),
        companyLid: (_p = extras.companyLid) !== null && _p !== void 0 ? _p : null,
        companyName: extras.companyName || '',
        sequenceNum: 0,
        crmExistingIntegrations: [],
        savedDatapoints: collectSavedDatapointIds(savedIntegrations || nested.integrations),
        wishlistData,
        isPartialProfile: false,
        changes: [],
    };
};
export const getContactMapKey = (contact) => contact.contactId || contact.id;
export const resolveContact = (contactsMap, contact) => {
    if (!contact) {
        return undefined;
    }
    if (typeof contact === 'string') {
        return contactsMap[contact];
    }
    const contactId = contact.contactId || '';
    const id = contact.id || '';
    return (contactId && contactsMap[contactId]) || (id && contactsMap[id]) || undefined;
};
export const getExistingContactMapKey = (contactsMap, contact) => {
    if (!contact) {
        return '';
    }
    if (typeof contact === 'string') {
        return contact;
    }
    const contactId = contact.contactId || '';
    const id = contact.id || '';
    if (contactId && contactsMap[contactId]) {
        return contactId;
    }
    if (id && contactsMap[id]) {
        return id;
    }
    return contactId || id || '';
};
/** Maps the employees of a prospecting search result to contacts, in result order. */
export const mapSearchEmployeesResultToContacts = (result) => {
    var _a;
    return ((_a = result.contacts) !== null && _a !== void 0 ? _a : []).map(employee => {
        var _a, _b, _c;
        return mapEmployeeToContact(employee, {
            requestId: result.requestId,
            companyLid: (_b = (_a = result.company) === null || _a === void 0 ? void 0 : _a.company_lid) !== null && _b !== void 0 ? _b : null,
            companyName: ((_c = result.company) === null || _c === void 0 ? void 0 : _c.name) || '',
        });
    });
};
/** Builds the `filters` of a prospecting employees request from the view's filters and title text. */
export const mapEmployeesSearchFilters = (selectedFilters, freeText) => {
    const filters = {};
    const { seniority, department, email, location } = selectedFilters;
    if (seniority.length) {
        filters.seniorities_labels = seniority;
    }
    if (department.length) {
        filters.departments = department;
    }
    if (email.length) {
        filters.existing_data_points = email.map(type => {
            const emailType = type.toLowerCase().replace('email', '').replace('direct', 'private').trim();
            return `${emailType}_email`;
        });
    }
    if (location.length) {
        filters.countries = location.map(item => ({ country: item.name }));
    }
    if (freeText) {
        filters.job_titles = [freeText];
    }
    return filters;
};
